/**
 * The keyboard, as a decision rather than as a listener.
 *
 * `docs/GAME-DESIGN.md` asks for input behind an abstraction, and this client
 * already has one for the mouse: `pointer-gesture.ts` decides whether a press
 * was a click or a drag, and the scene only feeds it positions and spends what
 * it hands back — *"a rule inside a Phaser scene is a rule nothing can
 * unit-test"*. This is the same shape for the other half of a desktop: a key
 * press in, a command out, no `window`, no React and no game state.
 *
 * ## Which keys, and why not the obvious ones
 *
 * **`t` opens the skill tree.** D2's own — the skill tree is `T` beside `A` for
 * the character sheet and `I` for the inventory.
 *
 * **`F1`–`F3` cast.** Also D2's: `F1`–`F8` are the skill hotkeys. The tempting
 * alternative is the number row, and it is already spoken for —
 * `docs/GAME-DESIGN.md`: *"Hotkeys 1–4 map to the [belt] columns and quaff from
 * the bottom row up"*. Binding a skill to `1` would be taking a key another
 * feature has been promised, and the day the belt lands one of the two would
 * have to move.
 *
 * Three of the eight, because there are three skills a class. D2 pairs a hotkey
 * with a click — the key selects, the mouse aims — and the click half needs the
 * scene to hit-test a body, which is a larger change than this slice; the
 * aiming is `session.ts`'s `nearestTargetId` for now and is stated as such in
 * the handover.
 *
 * **`Escape` closes.** The panel already handles it for a keystroke that lands
 * *inside* the dialog; this covers the far commoner case where focus is still on
 * the page and nothing in the panel has it.
 *
 * ## Two things every binding refuses
 *
 * A **modified** key is the browser's: `Ctrl-T` is a new tab and `Cmd-T` is the
 * same thing on a Mac, and a game that swallowed either would be a game a player
 * cannot leave.
 *
 * A **repeat** is not a second press. Holding `F1` repeats at whatever rate the
 * operating system is set to, and every repeat would be a message on the wire
 * and an allowance spent against the room's rate limit, for a cast the cooldown
 * is going to refuse anyway.
 */

/** D2's skill tree key. */
export const SKILL_TREE_KEY = 't';

/** D2's skill hotkeys, one per castable skill this slice has. Order **is** the slot number. */
export const SKILL_SLOT_KEYS: readonly string[] = ['F1', 'F2', 'F3'];

export type OverlayCommand =
  /** Show the skill tree, or hide it if it is already up. */
  | { readonly kind: 'toggle-skills' }
  /** Put away whatever is open. */
  | { readonly kind: 'close-panels' }
  /** Cast the skill in this slot, counting from zero. */
  | { readonly kind: 'cast'; readonly slot: number };

/**
 * The narrowest shape of a key event this module needs. A `KeyboardEvent`
 * satisfies it, and so does a plain object in a test — the same discipline
 * `skill-tree-model.ts` keeps with `StoppableEvent`.
 */
export interface KeyPress {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly altKey: boolean;
  readonly metaKey: boolean;
  /** True while the key is being held down. See the module note. */
  readonly repeat: boolean;
}

/** What this key press means, or `null` when it means nothing to the game. */
export const commandFor = (press: KeyPress): OverlayCommand | null => {
  if (press.repeat || press.ctrlKey || press.altKey || press.metaKey) return null;

  if (press.key === 'Escape') return { kind: 'close-panels' };
  if (press.key.toLowerCase() === SKILL_TREE_KEY) return { kind: 'toggle-skills' };

  const slot = SKILL_SLOT_KEYS.indexOf(press.key);

  return slot === -1 ? null : { kind: 'cast', slot };
};
